export const VIDEO_QUALITY_STORAGE_KEY = 'meeting.videoQualityMode';

export const VIDEO_QUALITY_PROFILES = Object.freeze({
  hd: Object.freeze({
    key: 'hd',
    label: 'HD',
    width: 1280,
    height: 720,
    frameRate: 24,
    maxBitrate: 2_200_000,
    maxSpatialLayer: 2,
  }),
  high: Object.freeze({
    key: 'high',
    label: 'High',
    width: 960,
    height: 540,
    frameRate: 20,
    maxBitrate: 1_300_000,
    maxSpatialLayer: 2,
  }),
  medium: Object.freeze({
    key: 'medium',
    label: 'Medium',
    width: 640,
    height: 360,
    frameRate: 15,
    maxBitrate: 750_000,
    // Keep all simulcast layers available. Source constraints and a
    // per-layer bitrate budget define the selected quality while receivers
    // can still fall back without a transport rebuild.
    maxSpatialLayer: 2,
  }),
  low: Object.freeze({
    key: 'low',
    label: 'Low',
    width: 480,
    height: 270,
    frameRate: 10,
    maxBitrate: 360_000,
    maxSpatialLayer: 2,
  }),
  verylow: Object.freeze({
    key: 'verylow',
    label: 'Very Low',
    width: 256,
    height: 144,
    frameRate: 6,
    maxBitrate: 140_000,
    maxSpatialLayer: 2,
  }),
});

export const VIDEO_QUALITY_OPTIONS = Object.freeze([
  Object.freeze({ key: 'auto', label: 'Auto' }),
  VIDEO_QUALITY_PROFILES.hd,
  VIDEO_QUALITY_PROFILES.high,
  VIDEO_QUALITY_PROFILES.medium,
  VIDEO_QUALITY_PROFILES.low,
  VIDEO_QUALITY_PROFILES.verylow,
]);

export const VIDEO_QUALITY_ORDER = Object.freeze([
  'verylow',
  'low',
  'medium',
  'high',
  'hd',
]);

export const normalizeVideoQualityMode = (value) => {
  const mode = String(value || '').trim().toLowerCase().replace(/[-_\s]/g, '');
  if (mode === 'auto') return 'auto';
  return VIDEO_QUALITY_PROFILES[mode] ? mode : 'auto';
};

export const getStoredVideoQualityMode = () => {
  if (typeof window === 'undefined') return 'auto';
  try {
    return normalizeVideoQualityMode(
      localStorage.getItem(VIDEO_QUALITY_STORAGE_KEY) || 'auto'
    );
  } catch {
    return 'auto';
  }
};

const finite = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Select a target from transport capacity, loss and latency—not the already
 * capped camera bitrate. This avoids Auto locking itself at Very Low after it
 * has reduced the sender.
 */
export const chooseAutomaticVideoProfile = ({
  availableOutgoingKbps,
  achievedVideoKbps,
  packetLoss,
  rttMs,
  qualityLimitationReason,
  fallback = 'medium',
} = {}) => {
  const available = finite(availableOutgoingKbps);
  const achieved = finite(achievedVideoKbps);
  const loss = Math.max(0, finite(packetLoss) ?? 0);
  const rtt = Math.max(0, finite(rttMs) ?? 0);
  const limitation = String(qualityLimitationReason || '').toLowerCase();

  // Candidate-pair availableOutgoingBitrate is the preferred capacity signal.
  // Achieved media bitrate is only a conservative fallback when the browser
  // does not expose that estimate.
  const capacity =
    available != null && available > 0
      ? available
      : limitation === 'bandwidth' && achieved != null
        ? achieved
        : null;

  let selected;
  if (loss >= 12 || rtt >= 700 || (capacity != null && capacity < 220)) {
    selected = 'verylow';
  } else if (
    loss >= 8 ||
    rtt >= 450 ||
    (capacity != null && capacity < 450)
  ) {
    selected = 'low';
  } else if (
    loss >= 4 ||
    rtt >= 300 ||
    (capacity != null && capacity < 850)
  ) {
    selected = 'medium';
  } else if (
    loss >= 2 ||
    rtt >= 200 ||
    (capacity != null && capacity < 1_600)
  ) {
    selected = 'high';
  } else if (
    capacity != null &&
    capacity >= 2_500 &&
    loss < 1.5 &&
    rtt < 160
  ) {
    selected = 'hd';
  } else if (capacity != null) {
    selected = 'high';
  } else {
    selected = VIDEO_QUALITY_PROFILES[fallback] ? fallback : 'medium';
  }

  // A CPU-bound encoder needs fewer pixels/frames even when bandwidth is
  // ample. The controller requires stable samples before it upgrades again.
  if (limitation === 'cpu') {
    const index = VIDEO_QUALITY_ORDER.indexOf(selected);
    return VIDEO_QUALITY_ORDER[Math.max(0, index - 1)] || 'verylow';
  }
  return selected;
};
